import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const Gallery = () => {
  const { t } = useTranslation();
  const [gallery, setGallery] = useState({ videos: [], siteImages: [], certificates: [] });
  const [activeTab, setActiveTab] = useState('videos');
  const [loading, setLoading] = useState(true);
  const [visibleSections, setVisibleSections] = useState({});
  
  // Video Modal State
  const [videoModal, setVideoModal] = useState({
    isOpen: false,
    videoUrl: '',
    title: ''
  });

  // Scroll animation observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setVisibleSections((prev) => ({
            ...prev,
            [entry.target.id]: entry.isIntersecting,
          }));
        });
      },
      { threshold: 0.15, rootMargin: '-80px' }
    );

    const sections = document.querySelectorAll('.animate-section');
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, [loading]);

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        closeVideoModal();
      }
    };

    if (videoModal.isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [videoModal.isOpen]);

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const response = await axios.get('/api/gallery');
        setGallery(response.data.data);
      } catch (error) {
        console.error('Error fetching gallery:', error);
        setGallery(staticGallery);
      } finally {
        setLoading(false);
      }
    };

    fetchGallery();
  }, []);

  const staticGallery = {
    videos: [
      { id: 1, title: 'Modular Operation By Al-Med Equipments at 21st century hospital, vapi', url: 'https://www.youtube.com/watch?v=xXObfn-czHQ', type: 'testimonial' },
      { id: 2, title: 'Automatic Shoe Wrapper Machine', url: 'https://www.youtube.com/watch?v=7yTbFvXKD78', type: 'product' },
      { id: 3, title: 'Interview of Mr Zakir Hussain, Almed Equipments', url: 'https://www.youtube.com/watch?v=MqTXWAxCocI&t=8s', type: 'company' },
      { id: 4, title: 'Almed equipment Mumbai - Class I air Quality on operation theatre', url: 'https://www.youtube.com/watch?v=m_oP7nbgwxk', type: 'product' },
      { id: 5, title: 'Dr.Manish from International Hospital Aurangabad Shares his experience', url: 'https://www.youtube.com/watch?v=IDkVZOe0BFc', type: 'testimonial' },
      { id: 6, title: 'Dr.Afzal Noor Khan from International Hospital shares his experience', url: 'https://www.youtube.com/watch?v=avILhV5NAq4', type: 'testimonial' }
    ],
    siteImages: [
      { id: 1, title: 'OT', description: 'Laminar Air Flow, Ductless Positive Pressure Systems & Pendant', url: 'https://almedequipment.com/images/gallery/site1.jpg' },
      { id: 2, title: 'OT', description: 'Ductless Positive Pressure Systems, Controlable OT Table & Epoxy Flooring', url: 'https://almedequipment.com/images/gallery/site2.jpg' },
      { id: 3, title: 'SS Modular OT', description: 'Laminar Air Flow, Ductless Positive Pressure Systems & Pendant', url: 'https://almedequipment.com/images/gallery/site3.jpg' },
      { id: 4, title: 'O2 Generation Plant', description: '', url: 'https://almedequipment.com/images/gallery/site4.jpg' },
      { id: 5, title: 'Modular OT', description: 'Laminar Air Flow, Ductless Positive Pressure Systems & Pendant', url: 'https://almedequipment.com/images/gallery/site5.jpg' },
      { id: 6, title: 'Modular OT', description: 'Ductless Positive Pressure Systems, Pendant & OT Light', url: 'https://almedequipment.com/images/gallery/site6.jpg' }
    ],
    certificates: [
      { id: 1, title: 'NQAC International', description: 'Certificate of compliance for AC Condensing Unit', url: 'https://almedequipment.com/images/certificates/1.jpg' },
      { id: 2, title: 'Medgate Today Magazine', description: 'Certificate of Honour', url: 'https://almedequipment.com/images/certificates/6.jpg' },
      { id: 3, title: 'NQAC International', description: 'Certificate of compliance for Anaesthesia Pendant', url: 'https://almedequipment.com/images/certificates/3.jpg' },
      { id: 4, title: 'Medgate Today Magazine', description: 'Certificate of Honour', url: 'https://almedequipment.com/images/certificates/4.png' },
      { id: 5, title: 'NQAC International', description: 'Negative Air Pressure Room', url: 'https://almedequipment.com/images/certificates/5.png' },
      { id: 6, title: 'NQAC International', description: 'Clean Room Illuminators', url: 'https://almedequipment.com/images/certificates/2.jpg' }
    ]
  };

  // Check if we have ANY Notion data (videos, siteImages, or certificates)
  const hasNotionData = 
    (gallery.videos && gallery.videos.length > 0) ||
    (gallery.siteImages && gallery.siteImages.length > 0) ||
    (gallery.certificates && gallery.certificates.length > 0);
  
  const displayGallery = hasNotionData ? gallery : staticGallery;

  // Check if URL is a YouTube video
  const isYouTubeUrl = (url) => {
    return url?.includes('youtube.com') || url?.includes('youtu.be');
  };

  // Get YouTube video ID
  const getYouTubeId = (url) => {
    const match = url?.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
    return match ? match[1] : null;
  };

  // Get YouTube embed URL
  const getYouTubeEmbedUrl = (url) => {
    const videoId = getYouTubeId(url);
    return videoId ? `https://www.youtube.com/embed/${videoId}?autoplay=1` : null;
  };

  // Get thumbnail for video (supports YouTube and custom thumbnails)
  const getVideoThumbnail = (video) => {
    // If video has a custom thumbnail image from Notion/Cloudinary
    if (video.image && video.image.length > 0) {
      return video.image;
    }
    // If it's a YouTube video, use YouTube thumbnail
    if (isYouTubeUrl(video.url)) {
      return `https://img.youtube.com/vi/${getYouTubeId(video.url)}/hqdefault.jpg`;
    }
    // Default placeholder for videos without thumbnails
    return '/video-placeholder.png';
  };

  // Get the actual video URL (from 'url' or 'videoUrl' property)
  const getVideoUrl = (video) => {
    return video.videoUrl || video.url || '';
  };

  // Open video modal
  const openVideoModal = (video) => {
    const videoUrl = getVideoUrl(video);
    setVideoModal({
      isOpen: true,
      videoUrl: videoUrl,
      title: video.title,
      isYouTube: isYouTubeUrl(videoUrl)
    });
  };

  // Close video modal
  const closeVideoModal = () => {
    setVideoModal({
      isOpen: false,
      videoUrl: '',
      title: '',
      isYouTube: false
    });
  };

  // Helper to get image URL (handles both 'url' from static and 'image' from Notion)
  const getImageUrl = (item) => item.url || item.image || '';

  const tabs = [
    { id: 'videos', label: t('gallery.tabs.videos'), count: displayGallery.videos?.length || 0 },
    { id: 'siteImages', label: t('gallery.tabs.installations'), count: displayGallery.siteImages?.length || 0 },
    { id: 'certificates', label: t('gallery.tabs.certificates'), count: displayGallery.certificates?.length || 0 }
  ];

  // Play icon component
  const PlayIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="white">
      <path d="M8 5v14l11-7z"/>
    </svg>
  );

  // Close icon component
  const CloseIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"></line>
      <line x1="6" y1="6" x2="18" y2="18"></line>
    </svg>
  );

  return (
    <div className="gallery-page" style={{marginTop: '70px'}}>
      {/* Hero Section */}
      <section className="hero" style={{minHeight: '40vh'}}>
        <div className="hero-content">
          <p className="hero-subtitle">{t('gallery.badge')}</p>
          <h1 className="hero-title">{t('gallery.title')}</h1>
          <p className="hero-description">
            {t('gallery.description')}
          </p>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery-content" className="section animate-section">
        <div className="container">
          {/* Tabs */}
          <div 
            className="gallery-tabs"
            style={{
              opacity: visibleSections['gallery-content'] ? 1 : 0,
              transform: visibleSections['gallery-content'] ? 'translateY(0)' : 'translateY(40px)',
              transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`gallery-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label} ({tab.count})
              </button>
            ))}
          </div>

          {loading ? (
            <div className="loading">
              <div className="spinner"></div>
            </div>
          ) : (
            <>
              {/* Videos Tab */}
              {activeTab === 'videos' && (
                <div>
                  <div className="section-header">
                    <h2 className="section-title">{t('gallery.videoSection.title')}</h2>
                    <p className="section-description">
                      {t('gallery.videoSection.description')}
                    </p>
                  </div>
                  <div className="gallery-grid">
                    {displayGallery.videos?.map((video) => (
                      <div 
                        key={video.id} 
                        className="gallery-item gallery-video-item"
                        onClick={() => openVideoModal(video)}
                      >
                        <div className="video-thumbnail-wrapper">
                          <img 
                            src={getVideoThumbnail(video)} 
                            alt={video.title}
                            onError={(e) => {
                              e.target.src = `https://img.youtube.com/vi/${getYouTubeId(video.url)}/hqdefault.jpg`;
                            }}
                          />
                          <div className="video-play-overlay">
                            <div className="video-play-button">
                              <PlayIcon />
                            </div>
                          </div>
                        </div>
                        <div className="gallery-item-content">
                          <h4 className="gallery-item-title">{video.title}</h4>
                          <p className="gallery-item-description">
                            {video.type === 'testimonial' ? '🎤 Testimonial' : video.type === 'product' ? '📦 Product Demo' : '🏢 Company'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Site Images Tab */}
              {activeTab === 'siteImages' && (
                <div>
                  <div className="section-header">
                    <h2 className="section-title">{t('gallery.tabs.installations')}</h2>
                    <p className="section-description">
                      Some collected shots of our installation sites
                    </p>
                  </div>
                  <div className="gallery-grid">
                    {displayGallery.siteImages?.map((image) => (
                      <div key={image.id} className="gallery-item">
                        <img src={getImageUrl(image)} alt={image.title} />
                        <div className="gallery-item-content">
                          <h4 className="gallery-item-title">{image.title}</h4>
                          <p className="gallery-item-description">{image.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Certificates Tab */}
              {activeTab === 'certificates' && (
                <div>
                  <div className="section-header">
                    <h2 className="section-title">{t('gallery.tabs.certificates')}</h2>
                    <p className="section-description">
                      With over 200+ Satisfied customers and Government projects trusting our patented and unique system
                    </p>
                  </div>
                  <div className="gallery-grid">
                    {displayGallery.certificates?.map((cert) => (
                      <div key={cert.id} className="gallery-item">
                        <img src={getImageUrl(cert)} alt={cert.title} />
                        <div className="gallery-item-content">
                          <h4 className="gallery-item-title">{cert.title}</h4>
                          <p className="gallery-item-description">{cert.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Video Modal */}
      {videoModal.isOpen && (
        <div className="video-modal-overlay" onClick={closeVideoModal}>
          <div className="video-modal" onClick={(e) => e.stopPropagation()}>
            <div className="video-modal-header">
              <h3 className="video-modal-title">{videoModal.title}</h3>
              <button 
                className="video-modal-close" 
                onClick={closeVideoModal}
                aria-label="Close video"
              >
                <CloseIcon />
              </button>
            </div>
            <div className="video-modal-content">
              {videoModal.isYouTube ? (
                <iframe
                  src={getYouTubeEmbedUrl(videoModal.videoUrl)}
                  title={videoModal.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              ) : (
                <video 
                  controls 
                  autoPlay
                  src={videoModal.videoUrl}
                >
                  Your browser does not support the video tag.
                </video>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;
